import { APIClient, getLoggedinUser } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";

const api = new APIClient();

// Helper function to get tenantId from logged-in user
const getTenantId = (): string | null => {
    const authUser = getLoggedinUser();
    return authUser?.tenantId || null;
};

// Interfaces
export interface Supplier {
    id: string;
    tenantId: string;
    tenantName?: string;
    supplierCode: string;
    name: string;
    address?: string;
    mobile?: string;
    isActive: boolean;
    contactName?: string;
    contactEmail?: string;
    notes?: string;
    isDeleted?: boolean;
    created?: string;
    modified?: string;
}

export interface SupplierCreatePayload {
    tenantId?: string; // Optional - backend may extract from JWT token
    supplierCode: string;
    name: string;
    address?: string;
    mobile?: string;
    isActive?: boolean;
    contactName?: string;
    contactEmail?: string;
    notes?: string;
}

export interface SupplierUpdatePayload {
    name?: string;
    address?: string;
    mobile?: string;
    isActive?: boolean;
    contactName?: string;
    contactEmail?: string;
    notes?: string;
}

// Service Methods
export const getSuppliers = async (
    tenantId?: string | null,
    activeOnly?: boolean
): Promise<Supplier[]> => {
    const finalTenantId = tenantId || getTenantId();

    if (!finalTenantId) {
        return Promise.reject(new Error("Organization ID is required"));
    }

    const endpoint = activeOnly
        ? `${url.GET_SUPPLIERS}/${finalTenantId}/active`
        : `${url.GET_SUPPLIERS}/${finalTenantId}`;

    return api.get<Supplier[]>(endpoint);
};

export const getSupplierById = async (id: string): Promise<Supplier> => {
    return api.get<Supplier>(`${url.GET_SUPPLIER_BY_ID}/${id}`);
};

export const createSupplier = async (supplier: SupplierCreatePayload): Promise<Supplier> => {
    const tenantId = supplier.tenantId || getTenantId();

    const payload: any = {
        supplierCode: supplier.supplierCode,
        name: supplier.name,
        isActive: supplier.isActive !== undefined ? supplier.isActive : true,
    };

    if (tenantId) {
        payload.tenantId = tenantId;
    }

    if (supplier.address) {
        payload.address = supplier.address;
    }

    if (supplier.mobile) {
        payload.mobile = supplier.mobile;
    }

    if (supplier.contactName) {
        payload.contactName = supplier.contactName;
    }

    if (supplier.contactEmail) {
        payload.contactEmail = supplier.contactEmail;
    }

    if (supplier.notes) {
        payload.notes = supplier.notes;
    }

    const response = await api.create<Supplier>(url.CREATE_SUPPLIER, payload);
    return response;
};

export const updateSupplier = async (id: string, supplier: SupplierUpdatePayload): Promise<Supplier> => {
    const payload: any = {
        id,
        name: supplier.name,
        address: supplier.address,
        mobile: supplier.mobile,
        isActive: supplier.isActive,
        contactName: supplier.contactName,
        contactEmail: supplier.contactEmail,
        notes: supplier.notes,
    };

    return api.put<Supplier>(url.UPDATE_SUPPLIER, payload);
};

export const deleteSupplier = (id: string): Promise<void> => {
    return api.delete<void>(`${url.DELETE_SUPPLIER}/${id}`);
};
